/**
 * Everything an org can have switched on or off (spec §6): features,
 * integrations and bots, each with the copy a Hub form shows and an
 * `availability` that says which orgs may have it at all.
 *
 * HUB-ONLY ON PURPOSE. The spec sketches `shared/registry/` for hub and
 * app; this repo cannot ship that cheaply (scripts/deploy-hub.sh rsyncs
 * hub/ alone and the hub image builds from hub/), so the hub owns the
 * vocabulary and a box only ever receives RESOLVED config as data
 * (@hub/lib/org-config). The ids here are the ids phase 1 pinned; their
 * order is the order the resolved lists come out in.
 */
export type Availability = "all" | { orgs: string[] };

export interface RegistryItem {
  id: string;
  label: string;
  description: string;
  availability: Availability;
}

// Literal, not imported from nymbl-org: that module imports org-switches,
// which imports this one. registry.test.ts pins it to NYMBL_ORG_SLUG.
const nymblOnly: Availability = { orgs: ["nymbl"] };

export const FEATURES: readonly RegistryItem[] = [
  {
    id: "browser_use",
    label: "Browser use",
    description: "A browser the assistant can drive on sites the owner allows.",
    availability: "all",
  },
  {
    id: "commitments_ledger",
    label: "Commitments Ledger",
    description: "Helm keeps your Tasks from your conversations.",
    availability: "all",
  },
  {
    id: "email_notifications",
    label: "Email notifications",
    description: "Needs-you and completion emails.",
    availability: "all",
  },
  {
    id: "fable_tier",
    label: "Fable tier",
    description: "The Fable model tier in the chat picker.",
    availability: "all",
  },
  { id: "home_panels", label: "Home panels", description: "Panels on Home.", availability: "all" },
  {
    id: "meetings",
    label: "Meetings",
    description: "Meeting library, briefs and recaps.",
    availability: "all",
  },
  {
    id: "missions",
    label: "Missions",
    description:
      "Helm works the tasks you owe, inside a share of your plan. Needs Commitments Ledger.",
    availability: "all",
  },
  {
    id: "nymblkb",
    label: "NymblKB",
    description: "Nymbl's private knowledge base, synced to the box.",
    availability: nymblOnly,
  },
  {
    id: "rooms",
    label: "Rooms",
    description: "Shared rooms with other people's Helms.",
    availability: "all",
  },
];

export const INTEGRATIONS: readonly RegistryItem[] = [
  { id: "clickup", label: "ClickUp", description: "Tasks and sprints.", availability: "all" },
  {
    id: "delyver",
    label: "Delyver",
    description: "Nymbl's delivery tool.",
    availability: nymblOnly,
  },
  {
    id: "github",
    label: "GitHub",
    description: "Repositories, pull requests and issues.",
    availability: "all",
  },
  {
    id: "google",
    label: "Google Drive",
    description: "Drive, Docs, Sheets and Slides.",
    availability: "all",
  },
  { id: "nymbl_crm", label: "Engage", description: "Nymbl's CRM.", availability: nymblOnly },
  {
    id: "nymbl_kb",
    label: "Nymbl KB",
    description: "Nymbl's knowledge base.",
    availability: nymblOnly,
  },
  {
    id: "nymbl_ops",
    label: "Nymbl Ops",
    description: "Nymbl's operations system.",
    availability: nymblOnly,
  },
  {
    id: "outlook",
    label: "Microsoft 365",
    description: "Outlook mail and calendar.",
    availability: "all",
  },
  {
    id: "slack",
    label: "Slack",
    description: "Channels and direct messages.",
    availability: "all",
  },
  { id: "teams", label: "Microsoft Teams", description: "Teams chats.", availability: "all" },
  {
    id: "zoom",
    label: "Zoom",
    description: "Meeting recordings and transcripts.",
    availability: nymblOnly,
  },
];

export const BOTS: readonly RegistryItem[] = [
  {
    id: "nymbl/analyst",
    label: "Analyst",
    description: "Answers questions over Nymbl's data.",
    availability: nymblOnly,
  },
  {
    id: "nymbl/client-prep",
    label: "Client prep",
    description: "Prepares for client meetings.",
    availability: "all",
  },
  {
    id: "nymbl/contract-desk",
    label: "Contract desk",
    description: "Reads and compares contracts.",
    availability: "all",
  },
  {
    id: "nymbl/delivery-pulse",
    label: "Delivery pulse",
    description: "Nymbl delivery health.",
    availability: nymblOnly,
  },
  {
    id: "nymbl/first-mate",
    label: "First Mate",
    description: "The orchestrating starter bot.",
    availability: "all",
  },
  {
    id: "nymbl/follow-through",
    label: "Follow-through",
    description: "Keeps commitments moving.",
    availability: "all",
  },
  {
    id: "nymbl/inbox-triage",
    label: "Inbox triage",
    description: "Sorts what needs you.",
    availability: "all",
  },
  {
    id: "nymbl/project-health",
    label: "Project health",
    description: "Nymbl project health from Nymbl Ops.",
    availability: nymblOnly,
  },
];

export type RegistryKind = "feature" | "integration" | "bot";

const BY_KIND: Record<RegistryKind, readonly RegistryItem[]> = {
  feature: FEATURES,
  integration: INTEGRATIONS,
  bot: BOTS,
};

export function registryItem(kind: RegistryKind, id: string): RegistryItem | null {
  return BY_KIND[kind].find((item) => item.id === id) ?? null;
}

/** Exact slug match; "all" is everyone. */
export function availableTo(item: RegistryItem, orgSlug: string): boolean {
  return item.availability === "all" || item.availability.orgs.includes(orgSlug);
}
